import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
  import { OptionSet } from '@/entities/optionSet';
  import { PropertyDataType } from '@/entities/entityTypeProperty';
  import { ValidationError } from '@/entities/validation';
  import { Icon } from '@/ui/components/icon/Icon';
  import { t } from '@/ui/i18n/i18n';
  import { validationError } from '@/ui/dictionary';
  import IconInfoCircle from '@/_legacy/components/Icons/IconInfoCircle';

  
export default /*#__PURE__*/_defineComponent({
  __name: 'PropertyFormatPicker',
  props: {
    name: {
      type: String,
      required: true,
    },
    dataType: {
      type: String as () => 'text' | 'dropdown',
      required: true,
    },
    optionSetId: {
      type: [Number, null],
      required: false,
      default: null,
    },
    optionSetName: {
      type: String,
      required: false,
      default: '',
    },
    optionSets: {
      type: Array as () => OptionSet[],
      required: true,
    },
    optionSetsLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
    errors: {
      type: Array as () => ValidationError[],
      required: false,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: [
    'select-format',
    'select-option-set',
    'edit-option-set',
    'delete-option-set',
    'create-option-set',
  ],
  setup(__props, { emit }) {

const props = __props;

  

  

  const pickerRef = ref<HTMLElement | null>(null);
  const isOpen = ref(false);
  const hoveredFormat = ref<PropertyDataType | null>(null);
  const selectedFormat = ref<PropertyDataType>(props.dataType as PropertyDataType);
  const selectedOptionSetId = ref<number | null>(props.optionSetId);

  // Watch for changes to props and update local state
  watch(() => props.optionSetId, newValue => {
    selectedOptionSetId.value = newValue;
  });

  watch(() => props.dataType, newValue => {
    selectedFormat.value = newValue as PropertyDataType;
  });

  // Error handling
  const errorsForThisField = computed(() => {
    return props.errors.filter(error => {
      // Check for errors on dataType, optionSetId, or the parent field
      return error.field === `${props.name}.dataType`
        || error.field === `${props.name}.optionSetId`
        || error.field === props.name;
    });
  });

  const hasError = computed(() => errorsForThisField.value.length > 0);

  const errorMessage = computed(() => {
    if (!hasError.value) {
      return '';
    }

    const error = errorsForThisField.value[0];

    return validationError(t('entity-type.input-format'), error);
  });

  const displayText = computed(() => {
    if (props.dataType === PropertyDataType.Text) {
      return t('entity-type.format-text');
    }
    if (props.dataType === PropertyDataType.Dropdown && props.optionSetName) {
      return `${t('entity-type.format-dropdown')} (${props.optionSetName})`;
    }
    if (props.dataType === PropertyDataType.Dropdown) {
      return t('entity-type.select-input-format');
    }

    return t('entity-type.select-input-format');
  });

  const togglePicker = () => {
    if (!props.disabled) {
      isOpen.value = !isOpen.value;
    }
  };

  const handleDropdownHover = () => {
    hoveredFormat.value = PropertyDataType.Dropdown;
  };

  const handleEditOptionSet = (optionSetId: number, event: Event) => {
    event.stopPropagation();
    emit('edit-option-set', optionSetId);
  };

  const handleDeleteOptionSet = (optionSetId: number, event: Event) => {
    event.stopPropagation();
    emit('delete-option-set', optionSetId);
  };

  const selectFormat = (format: PropertyDataType) => {
    selectedFormat.value = format;

    if (format === PropertyDataType.Text) {
      emit('select-format', PropertyDataType.Text);
      isOpen.value = false;

      return;
    }

    emit('create-option-set');
  };

  const selectOptionSet = (optionSetId: number) => {
    selectedOptionSetId.value = optionSetId;
    selectedFormat.value = PropertyDataType.Dropdown;
    emit('select-format', PropertyDataType.Dropdown);
    emit('select-option-set', optionSetId);
    isOpen.value = false;
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (pickerRef.value && !pickerRef.value.contains(event.target as Node)) {
      isOpen.value = false;
      hoveredFormat.value = null;
    }
  };

  const handleEscape = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && isOpen.value) {
      isOpen.value = false;
      hoveredFormat.value = null;
    }
  };

  onMounted(() => {
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
  });

  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
    document.removeEventListener('keydown', handleEscape);
  });

return { __sfc: true,props, emit, pickerRef, isOpen, hoveredFormat, selectedFormat, selectedOptionSetId, errorsForThisField, hasError, errorMessage, displayText, togglePicker, handleDropdownHover, handleEditOptionSet, handleDeleteOptionSet, selectFormat, selectOptionSet, handleClickOutside, handleEscape, PropertyDataType, Icon, IconInfoCircle }
}

})